/**
 * The item timeline, quieter (case round, finding 8): dozens of Merlin
 * housekeeping lines ("summary revised, join keys updated") read oldest
 * first and buried what people did. Here the timeline reads newest first,
 * people's actions and the delivery lines show in full, and each run of
 * housekeeping lines folds into one line that opens on a click.
 *
 * Also: why the item came to the reader and who sent it (finding 5), read
 * from the same events.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorThreadEventRead } from '../../services/qcpInvestigations';

/** Lines Merlin writes while it keeps its own notes current. */
const HOUSEKEEPING: ReadonlySet<string> = new Set([
  'updated',
  'case_proposal_updated',
  'investigation_filed',
]);

export function isHousekeeping(ev: Pick<DoorThreadEventRead, 'event_type' | 'actor_kind'>): boolean {
  return HOUSEKEEPING.has(ev.event_type) && ev.actor_kind !== 'human';
}

export type TimelineEntry =
  | { kind: 'event'; key: string; event: DoorThreadEventRead }
  | { kind: 'folded'; key: string; events: DoorThreadEventRead[] };

/** Newest first; each run of housekeeping lines becomes one folded entry. */
export function quietTimeline(events: DoorThreadEventRead[]): TimelineEntry[] {
  const newest = [...events]
    .map((event, i) => ({ event, i }))
    .sort(
      (a, b) =>
        new Date(b.event.created_at).getTime() - new Date(a.event.created_at).getTime() || b.i - a.i,
    );
  const out: TimelineEntry[] = [];
  for (const { event, i } of newest) {
    const last = out[out.length - 1];
    if (isHousekeeping(event)) {
      if (last?.kind === 'folded') last.events.push(event);
      else out.push({ kind: 'folded', key: `fold-${i}`, events: [event] });
    } else {
      out.push({ kind: 'event', key: `ev-${i}`, event });
    }
  }
  return out;
}

/** "Merlin updated its notes 12 times" for a folded run. */
export function foldedLabel(events: DoorThreadEventRead[]): string {
  const n = events.length;
  return n === 1 ? 'Merlin updated its notes once' : `Merlin updated its notes ${n} times`;
}

/**
 * Who put the item where it is now: the newest (re)routing line. Merlin's
 * own routing reads "Merlin"; a reroute by a person names them when the
 * server says who, else 'by hand'. With no routing line in what the
 * timeline returns (it can stop short on a long item), nothing is claimed.
 */
export function sentBy(
  events: ReadonlyArray<DoorThreadEventRead> | null | undefined,
): string | null {
  const routing = [...(events ?? [])]
    .filter((e) => e.event_type === 'rerouted' || e.event_type === 'routed')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  if (!routing) return null;
  if (routing.actor_kind === 'merlin') return 'Merlin';
  const label = routing.actor_label?.trim();
  if (!label) return 'by hand';
  return label.toLowerCase() === 'merlin' ? 'Merlin' : label;
}
